import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Alert,
  LinearProgress,
  Divider,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  SmartToy as AIIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import api from '../utils/api';

interface UploadedDocument {
  name: string;
  size: number;
  path: string;
  uploaded_at: number;
}

interface AITrainingGenerationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AITrainingGenerationDialog: React.FC<AITrainingGenerationDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [targetRoles, setTargetRoles] = useState('');
  const [regulatoryStandards, setRegulatoryStandards] = useState('');
  const [trainingPlan, setTrainingPlan] = useState('');
  const [planGenerated, setPlanGenerated] = useState(false);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);
  const [providedLinks, setProvidedLinks] = useState<string[]>([]);
  const [newLink, setNewLink] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      initializeSession();
    } else {
      resetDialog();
    }
  }, [open]);

  const initializeSession = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await api.post('/ai-training-sessions/', {});
      setSessionId(response.data.session_id);
    } catch (err: any) {
      setError('Failed to create session: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  const resetDialog = () => {
    setSessionId(null);
    setTitle('');
    setDescription('');
    setTargetAudience('');
    setTargetRoles('');
    setRegulatoryStandards('');
    setTrainingPlan('');
    setPlanGenerated(false);
    setUploadedDocuments([]);
    setProvidedLinks([]);
    setNewLink('');
    setIsGenerating(false);
    setGenerationProgress(0);
    setCurrentStep('');
    setError(null);
  };

  const handleGeneratePlan = async () => {
    if (!sessionId || !title || !description) {
      setError('Please provide a title and description before generating the training plan.');
      return;
    }

    try {
      setIsGeneratingPlan(true);
      setError(null);

      // Update session with context information
      await api.patch(`/ai-training-sessions/${sessionId}/`, {
        title,
        description,
        target_audience: targetAudience,
        target_roles: targetRoles,
        regulatory_standards: regulatoryStandards
      });

      // Add provided links if any
      console.log('[DEBUG LINKS] providedLinks:', providedLinks);
      console.log('[DEBUG LINKS] providedLinks.length:', providedLinks.length);
      if (providedLinks.length > 0) {
        try {
          console.log('[DEBUG LINKS] Calling add_links endpoint with:', providedLinks);
          const response = await api.post(`/ai-training-sessions/${sessionId}/add_links/`, {
            links: providedLinks
          });
          console.log('[DEBUG LINKS] add_links response:', response.data);
          console.log(`✅ Added ${providedLinks.length} link(s) to session`);
        } catch (linkError) {
          console.error('❌ Failed to add links:', linkError);
          // Don't fail the entire process if links fail
        }
      } else {
        console.log('[DEBUG LINKS] No links to add');
      }

      // Create a comprehensive prompt for training plan generation
      const comprehensivePrompt = `Please create a detailed training plan for "${title}". 

TRAINING CONTEXT:
- Description: ${description}
- Target Audience: ${targetAudience || 'Not specified'}
- Target Roles/Industries: ${targetRoles || 'Not specified'}
- Regulatory Standards: ${regulatoryStandards || 'Not specified'}

REQUIRED TRAINING PLAN STRUCTURE:

1. **Learning Objectives** (4-6 specific, measurable objectives):
   - Use action verbs (Identify, Demonstrate, Implement, etc.)
   - Be specific to the training topic
   - Include regulatory compliance where applicable

2. **Training Sections** (4-6 sections, each with detailed descriptions):
   For each section, provide:
   - Section Title
   - Detailed Description (2-3 sentences explaining what will be covered)
   - Content Outline (specific topics, procedures, regulations to be taught)
   - Learning Outcomes (what learners will know/be able to do after this section)

3. **Mid-Training Quiz** (EXACTLY 1 quiz with 1-2 questions):
   - Place ONE quiz block in a MIDDLE section (e.g., section 3 or 4 of 6)
   - Create 1-2 multiple-choice questions with 4 options each
   - Include the correct answer and brief explanations
   - Questions should test understanding of key concepts learned so far
   - This provides formative assessment but does not affect the final score

4. **Final Assessment** (EXACTLY 1 test with 5-8 questions):
   - Place ONE test block in the LAST section ONLY
   - Create 5-8 comprehensive multiple-choice questions with 4 options each
   - Include scenario-based questions that test overall understanding
   - Cover regulatory compliance and practical application
   - Include correct answers and detailed explanations
   - This test determines the training score and passing/failing

FORMAT REQUIREMENTS:
- Use clear headings and subheadings
- Provide specific, actionable content descriptions
- Include regulatory references where applicable
- Make content relevant to the target audience and industry
- Ensure the training can be completed in under 15 minutes

Please create a comprehensive training plan that includes all of these elements in a structured, easy-to-review format.`;

      // Send comprehensive message to generate training plan
      const response = await api.post(`/ai-training-sessions/${sessionId}/chat/`, {
        message: comprehensivePrompt
      }, {
          timeout: 30000 // 30 seconds - backend should respond immediately with 202
      });

      // Check if it's async (202 Accepted) or sync (200 OK)
      if (response.status === 202) {
        console.log('[AI Training] Plan generation started asynchronously, polling for completion...');
        // Poll for plan completion
        const pollInterval = setInterval(async () => {
          try {
            const progressResponse = await api.get(`/ai-training-sessions/${sessionId}/get_progress/`);
            const chatProcessing = progressResponse.data.progress?.chat_processing;
            
            console.log('[AI Training] Polling chat status:', chatProcessing);
            
            if (chatProcessing?.status === 'completed' && chatProcessing?.success) {
              clearInterval(pollInterval);
              console.log('Full AI response length:', chatProcessing.response?.length);
              setTrainingPlan(chatProcessing.response);
              setPlanGenerated(true);
              setIsGeneratingPlan(false);
            } else if (chatProcessing?.status === 'failed' || (chatProcessing?.status === 'completed' && !chatProcessing?.success)) {
              clearInterval(pollInterval);
              const errorMsg = chatProcessing?.error || chatProcessing?.message || 'Unknown error';
              console.error('[AI Training] Plan generation failed:', errorMsg);
              setError('Failed to generate training plan: ' + errorMsg);
              setIsGeneratingPlan(false);
            }
          } catch (pollErr: any) {
            console.error('[AI Training] Error polling plan status:', pollErr);
            if (pollErr.response?.status === 404) {
              clearInterval(pollInterval);
              setError('Session not found');
              setIsGeneratingPlan(false);
            }
          }
        }, 2000); // Poll every 2 seconds
      } else if (response.data.success) {
        // Sync response (fallback when Celery is not available)
        console.log('Full AI response length:', response.data.response?.length);
        setTrainingPlan(response.data.response);
        setPlanGenerated(true);
        setIsGeneratingPlan(false);
      } else {
        setError(response.data.message || 'Failed to generate training plan');
        setIsGeneratingPlan(false);
      }
    } catch (err: any) {
      setError('Failed to generate training plan: ' + (err.response?.data?.detail || err.message));
      setIsGeneratingPlan(false);
    }
  };

  const pollChatCompletion = () => {
    let isGenerationStarted = false; // Flag to prevent double-calling
    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/ai-training-sessions/${sessionId}/get_progress/`);
        
        const chatProcessing = response.data.progress?.chat_processing;
        
        console.log('[AI Training] Polling chat completion status:', chatProcessing);
        
        if (chatProcessing?.status === 'completed' && chatProcessing?.success) {
          clearInterval(interval);
          
          // Prevent double-calling if multiple poll callbacks are in flight
          if (isGenerationStarted) {
            console.log('[AI Training] Generation already started, skipping duplicate call');
            return;
          }
          isGenerationStarted = true;
          
          console.log('[AI Training] Chat processing completed, starting generation...');
          
          // Start the training generation process
          try {
            const generateResponse = await api.post(`/ai-training-sessions/${sessionId}/start_generation/`, {}, {
              timeout: 300000 // 5 minutes (should return immediately with async)
            });

            console.log('[AI Training] Generation response:', generateResponse.data);

            if (generateResponse.data.success) {
              console.log('[AI Training] Generation started successfully, polling progress...');
              // Start polling for progress
              pollProgress();
            } else {
              console.error('[AI Training] Generation failed:', generateResponse.data.message);
              setError(generateResponse.data.message || 'Failed to start training generation');
              setIsGenerating(false);
            }
          } catch (err: any) {
            console.error('[AI Training] Error starting generation:', err);
            setError('Failed to start training generation: ' + (err.response?.data?.detail || err.response?.data?.message || err.message));
            setIsGenerating(false);
          }
        } else if (chatProcessing?.status === 'failed' || (chatProcessing?.status === 'completed' && !chatProcessing?.success)) {
          clearInterval(interval);
          const errorMsg = chatProcessing?.error || chatProcessing?.message || 'Unknown error';
          console.error('[AI Training] Chat processing failed:', errorMsg);
          setError('Failed to process training plan: ' + errorMsg);
          setIsGenerating(false);
        } else {
          // Still processing
          console.log('[AI Training] Chat processing in progress...');
          setCurrentStep('Processing training plan with AI...');
        }
      } catch (err: any) {
        console.error('[AI Training] Error polling chat status:', err);
        // Don't clear interval on transient errors, only on fatal ones
        if (err.response?.status === 404) {
          clearInterval(interval);
          setError('Session not found');
          setIsGenerating(false);
        }
      }
    }, 2000); // Poll every 2 seconds
  };

  const handleGenerateTraining = async () => {
    if (!sessionId || !trainingPlan) {
      setError('Please generate and review the training plan first.');
      return;
    }

    try {
      console.log('[AI Training] Starting training generation...');
      setIsGenerating(true);
      setError(null);
      setGenerationProgress(0);
      setCurrentStep('Sending training plan to AI...');

      console.log('[AI Training] Sending training plan to chat endpoint...');
      // Send the edited plan to the AI for parsing and training creation
      const response = await api.post(`/ai-training-sessions/${sessionId}/chat/`, {
        message: `Please parse this training plan and create the actual training content:

${trainingPlan}

Based on this plan, please generate the comprehensive training content with detailed text blocks, quiz questions, and final assessment questions.`
      }, {
        timeout: 30000 // 30 seconds - backend should respond immediately with 202
      });

      console.log('[AI Training] Chat response:', response.data);

      // Check if it's async (202 Accepted) or sync (200 OK)
      if (response.status === 202) {
        console.log('[AI Training] Chat processing started asynchronously, polling for completion...');
        setCurrentStep('Processing training plan with AI...');
        // Start polling for chat completion
        pollChatCompletion();
      } else if (response.data.success) {
        // Sync response (fallback when Celery is not available)
        console.log('[AI Training] Chat successful (sync), starting generation...');
        // Start the training generation process
        const generateResponse = await api.post(`/ai-training-sessions/${sessionId}/start_generation/`, {}, {
          timeout: 300000 // 5 minutes (should return immediately with async)
        });

        console.log('[AI Training] Generation response:', generateResponse.data);

        if (generateResponse.data.success) {
          console.log('[AI Training] Generation started successfully, polling progress...');
          // Start polling for progress
          pollProgress();
        } else {
          console.error('[AI Training] Generation failed:', generateResponse.data.message);
          setError(generateResponse.data.message || 'Failed to start training generation');
          setIsGenerating(false);
        }
      } else {
        console.error('[AI Training] Chat processing failed:', response.data.message);
        setError(response.data.message || 'Failed to process training plan');
        setIsGenerating(false);
      }
    } catch (err: any) {
      console.error('[AI Training] Error during generation:', err);
      console.error('[AI Training] Error response:', err.response?.data);
      setError('Failed to generate training: ' + (err.response?.data?.detail || err.response?.data?.message || err.message));
      setIsGenerating(false);
    }
  };

  const pollProgress = () => {
    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/ai-training-sessions/${sessionId}/get_progress/`);
        
        // Handle both old and new response formats
        let progress = 0;
        let currentStep = 'Processing...';
        const sessionStatus = response.data.status || 'in_progress';
        
        if (typeof response.data.progress === 'object' && response.data.progress !== null) {
          // New format: progress is an object
          progress = response.data.progress.progress_percentage || 0;
          currentStep = response.data.progress.current_step || 'Processing...';
        } else {
          // Old format: progress is a number
          progress = response.data.progress || 0;
          currentStep = response.data.current_step || 'Processing...';
        }
        
        setGenerationProgress(progress);
        setCurrentStep(currentStep);

        // Check for completion or failure
        if (sessionStatus === 'completed' || progress >= 100) {
          clearInterval(interval);
          setIsGenerating(false);
          onSuccess();
          onClose();
        } else if (sessionStatus === 'failed') {
          clearInterval(interval);
          setIsGenerating(false);
          setError(currentStep || 'Training generation failed');
        }
      } catch (err) {
        console.error('Error polling progress:', err);
        clearInterval(interval);
        setIsGenerating(false);
        setError('Failed to track generation progress');
      }
    }, 1000);

    // Clear interval after 5 minutes as safety
    setTimeout(() => {
      clearInterval(interval);
      if (isGenerating) {
        setIsGenerating(false);
        setError('Generation timed out');
      }
    }, 300000);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    console.log('📎 File selected:', file?.name, file?.size);
    
    if (!file || !sessionId) {
      console.log('❌ No file or session ID:', { file: !!file, sessionId });
      return;
    }

    // Check file size (20MB limit)
    if (file.size > 20 * 1024 * 1024) {
      setError('File size exceeds 20MB limit');
      return;
    }

    try {
      console.log('📤 Uploading document to session:', sessionId);
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/ai-training-sessions/${sessionId}/upload_document/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('📥 Upload response:', response.data);

      if (response.data.success) {
        console.log('✅ Document uploaded successfully:', response.data.metadata);
        setUploadedDocuments(prev => [...prev, response.data.metadata]);
      } else {
        console.log('❌ Upload failed:', response.data.message);
        setError(response.data.message || 'Failed to upload document');
      }
    } catch (err: any) {
      console.error('❌ Upload error:', err);
      setError('Failed to upload document: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRemoveDocument = async (filename: string) => {
    if (!sessionId) return;

    try {
      await api.delete(`/ai-training-sessions/${sessionId}/upload_document/`, {
        data: { filename }
      });
      setUploadedDocuments(prev => prev.filter(doc => doc.name !== filename));
    } catch (err: any) {
      setError('Failed to remove document: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleAddLink = () => {
    if (newLink.trim() && !providedLinks.includes(newLink.trim())) {
      setProvidedLinks(prev => [...prev, newLink.trim()]);
      setNewLink('');
    }
  };

  const handleRemoveLink = (linkToRemove: string) => {
    setProvidedLinks(prev => prev.filter(link => link !== linkToRemove));
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleAddLink();
    }
  };

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <AIIcon sx={{ mr: 1, color: 'primary.main' }} />
            Create Training with AI
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Basic Information */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Training Information
          </Typography>
          <TextField
            fullWidth
            label="Training Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter a descriptive title for your training..."
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Training Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what this training will cover..."
          />
        </Box>

        {/* Training Context */}
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="training-context-content"
            id="training-context-header"
          >
            <Typography variant="h6">
              Training Context (Optional)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These fields, if populated, will override the matching Company Settings for this specific training generation session. 
              Leave blank to use your company's default settings.
            </Typography>
            <TextField
              fullWidth
              label="Target Audience"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder="Who will be participating in this training? (e.g., new employees, experienced workers, mixed groups)"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Target Roles/Industries"
              value={targetRoles}
              onChange={(e) => setTargetRoles(e.target.value)}
              placeholder="Specific roles or industries being targeted (e.g., construction, maintenance, warehouse operations)"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Regulatory Standards"
              value={regulatoryStandards}
              onChange={(e) => setRegulatoryStandards(e.target.value)}
              placeholder="Specific regulatory or company standards that need to be covered (e.g., OSHA, company policies, industry standards)"
            />
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 2 }} />

        {/* Upload Documents */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Upload Documents
          </Typography>
          <Box mb={2}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
            />
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 2 }}
            >
              Upload Document
            </Button>
            <Typography variant="caption" display="block" color="text.secondary">
              Supported formats: PDF, DOC, DOCX, TXT, JPG, PNG, GIF (Max 20MB)
            </Typography>
          </Box>

          {uploadedDocuments.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Uploaded Documents:
              </Typography>
              <List dense>
                {uploadedDocuments.map((doc, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={doc.name}
                      secondary={`Size: ${(doc.size / 1024 / 1024).toFixed(2)} MB`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveDocument(doc.name)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>

        {/* Provide Links */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Provide Links
          </Typography>
          <Box display="flex" gap={1} mb={2}>
            <TextField
              fullWidth
              label="Training Content Link"
              value={newLink}
              onChange={(e) => setNewLink(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter URL to training content, images, or videos..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleAddLink}
              disabled={!newLink.trim()}
            >
              Add
            </Button>
          </Box>

          {providedLinks.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Provided Links:
              </Typography>
              <List dense>
                {providedLinks.map((link, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                          {link}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveLink(link)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>

        {/* Training Plan Generation */}
        {!planGenerated ? (
          <Box mb={3}>
            <Button
              variant="contained"
              size="large"
              startIcon={isGeneratingPlan ? <CircularProgress size={20} /> : <AIIcon />}
              onClick={handleGeneratePlan}
              disabled={!title || !description || isGeneratingPlan}
              fullWidth
            >
              {isGeneratingPlan ? 'Generating Training Plan...' : 'Create Training Plan'}
            </Button>
          </Box>
        ) : (
          <Box mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6">
                Training Plan
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {trainingPlan.length.toLocaleString()} characters
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review and edit the training plan below. You can modify any section before generating the training.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={15}
              maxRows={25}
              value={trainingPlan}
              onChange={(e) => setTrainingPlan(e.target.value)}
              placeholder="Training plan will appear here..."
              sx={{ 
                mb: 2,
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  lineHeight: 1.5
                }
              }}
              InputProps={{
                style: {
                  maxHeight: '600px',
                  overflowY: 'auto'
                }
              }}
            />
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={() => setPlanGenerated(false)}
              >
                Regenerate Plan
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={isGenerating ? <CircularProgress size={20} /> : <AIIcon />}
                onClick={handleGenerateTraining}
                disabled={isGenerating || !trainingPlan.trim()}
                fullWidth
              >
                {isGenerating ? 'Generating Training...' : 'Generate Training'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Generation Progress */}
        {isGenerating && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {currentStep}
            </Typography>
            <LinearProgress variant="determinate" value={generationProgress} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {generationProgress}% Complete
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isGenerating}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AITrainingGenerationDialog;
